import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import { DNABox, DNAButton, DNAText, Iffy, util } from '@alucio/lux-ui/src';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { HubSectionType, HubType, SharedFileHubSetting } from '@alucio/aws-beacon-amplify/src/models';
import {
  WidgetContentCardProps,
  WidgetCardHeader,
  WidgetCardBody,
  WidgetContextMenu,
  widgetContentCardStyles,
} from 'src/screens/Hubs/EditHub/Widgets/WidgetContentCard';
import { useHubsState } from 'src/state/context/Hubs/HubsStateProvider.proxy';
import ContentSearchBar from 'src/components/ContentSearchBar/ContentSearchBar';
import DNARearranger, { ModifiersListEnum } from 'src/components/DnD/DNARearranger';
import SharedFilesDocumentGroup from 'src/screens/Hubs/EditHub/Widgets/SharedFilesComponents/SharedFilesDocumentGroup';
import { SharedFilesProvider, useSharedFilesState } from './SharedFilesComponents/SharedFilesProvider';

const DNAREARRANGER_MODIFIERS = [ModifiersListEnum.restrictToVerticalAxis];

export const styles = StyleSheet.create({
  emptyStateTextBox: {
    marginTop: 24,
  },
  documentGroupsContainer: {
    borderColor: colors['color-gray-80'],
    borderWidth: 1,
  },
  associatedFilesGroupContainer: {
    paddingLeft: 24,
    paddingBottom: 24,
  },
  associatedFilesGroupBorderBox: {
    borderColor: colors['color-gray-80'],
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderBottomWidth: 1,
  },
  bottomBorder: {
    borderBottomColor: colors['color-gray-80'],
    borderBottomWidth: 1,
  },
  draggableDocumentGroup: {
    borderBottomColor: colors['color-gray-80'],
    borderBottomWidth: 1,
  },
  draggableDocumentGroupLast: {
    borderBottomWidth: 0,
  },
  associatedFilesGroupHeader: {
    paddingHorizontal: 16,
    paddingVertical: 4,
    backgroundColor: colors['color-gray-10'],
    borderBottomColor: colors['color-gray-80'],
    borderBottomWidth: 1,
  },
  row: {
    padding: 24,
  },
  rowHovered: {
    backgroundColor: colors['color-gray-10'],
  },
  documentThumbnailBorder: {
    borderColor: colors['color-gray-80'],
    borderWidth: 1,
    marginRight: 16,
    maxWidth: 122,
  },
  attachedFileIcon: {
    height: 30,
    color: colors['color-gray-300']
  },
  attachedFileThumbnailBox: {
    minWidth: 120,
    minHeight: 65,
    backgroundColor: colors['color-gray-10'],
  },
  errMsgContainer: {
    marginTop: 5,
  },
});

const SharedFiles: React.FC = () => {
  const { showFullSizeBtn, hubORM } = useHubsState()
  const {
    widget,
    activeItems,
    selectedIds,
    hasReachedMaxNum,
    errorMsg,
    isSearchBarVisible,
    setIsSearchBarVisible,
    handleShowSearchBar,
    handleSelectItem,
    handleDeleteItem,
    handleReorderItems,
  } = useSharedFilesState()

  const [activeItem, setActiveItem] = useState<SharedFileHubSetting>()

  const itemIds = useMemo(() => activeItems.map(sharedFile => sharedFile.id), [activeItems])

  const isLockedTemplateInstance = useMemo(() => (
    hubORM?.model.hubType === HubType.TEMPLATE_INSTANCE && widget.locked
  ), [hubORM?.model.hubType, widget.locked])

  const canEditWidget = useMemo(() => (
    hubORM?.model.hubType !== HubType.TEMPLATE_INSTANCE || !widget.locked
  ), [hubORM?.model.hubType, widget.locked])

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const activeSharedFile = activeItems.find(sharedFile => sharedFile.id === event.active.id)
    setActiveItem(activeSharedFile)
  }, [activeItems])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    handleReorderItems(String(event.active.id), event.over?.id ? String(event.over.id) : undefined)
    setActiveItem(undefined)
  }, [handleReorderItems])

  const rightElement = useMemo(() => (
    <Iffy is={canEditWidget}>
      <DNABox alignY="center" spacing="sm">
        <DNAButton
          testID='hub-add-file-btn'
          appearance="outline"
          status="tertiary"
          iconLeft="plus"
          onPress={handleShowSearchBar}
          children={showFullSizeBtn ? 'Add file' : undefined}
        />
        <Iffy is={hubORM?.model.hubType !== HubType.TEMPLATE_INSTANCE}>
          <WidgetContextMenu widgetType={HubSectionType.SHARED_FILES} id={widget.id} />
        </Iffy>
      </DNABox>
    </Iffy>
  ), [canEditWidget, handleShowSearchBar, showFullSizeBtn, widget.locked])

  if (widget.type !== HubSectionType.SHARED_FILES) return null
  return (       
    <>
      <WidgetCardHeader
        widget={widget}
        rightElement={rightElement}
      />
      <WidgetCardBody>
        <ContentSearchBar
          onSelectItem={handleSelectItem}
          selectedIds={selectedIds}
          variant="HUBS"
          isVisible={isSearchBarVisible}
          setIsVisible={setIsSearchBarVisible}
          disable={hasReachedMaxNum}
        />
        <Iffy is={isSearchBarVisible && errorMsg.length}>
          <DNABox style={styles.errMsgContainer}>
            {errorMsg.map(err => <DNAText status="danger">{err}</DNAText>)}
          </DNABox>
        </Iffy>
        <DNABox fill appearance="col" alignY="center" style={isSearchBarVisible ? styles.emptyStateTextBox : undefined}>
          <Iffy is={!selectedIds.length}>
            <DNAText status="subtle">Add files to see them appear here</DNAText>
          </Iffy>
          <Iffy is={activeItems.length}>
            <DNABox testID='shared-files-document-item' fill appearance="col" style={styles.documentGroupsContainer}>
              <Iffy is={isLockedTemplateInstance}>
                {activeItems.map((sharedFile, index) => (
                  <SharedFilesDocumentGroup
                    key={sharedFile.id}
                    sharedFile={sharedFile}
                    isLastItem={index === activeItems.length - 1}
                    handleDeleteItem={handleDeleteItem}
                  />
                ))}
              </Iffy>
              <Iffy is={!isLockedTemplateInstance}>
                <DNARearranger
                  collisionDetection="closestCenter"
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  modifiers={DNAREARRANGER_MODIFIERS}
                  items={itemIds}
                  strategy="verticalListSortingStrategy"
                  overlay={activeItem && (
                    <DNARearranger.SortableItem
                      id={activeItem.id}
                      itemId={activeItem.id}
                      containerId="shared-files-container"
                      handle
                      iconAlignY="start"
                      boxContainerStyle={styles.draggableDocumentGroup}
                    >
                      <DNABox fill appearance="col">
                        <SharedFilesDocumentGroup
                          sharedFile={activeItem}
                          isLastItem
                          handleDeleteItem={handleDeleteItem}
                        />
                      </DNABox>
                    </DNARearranger.SortableItem>
                  )}
                >
                  {activeItems.map((sharedFile, index) => {
                    const isLastItem = index === activeItems.length - 1
                    return (
                      <DNARearranger.SortableItem
                        key={sharedFile.id}
                        id={sharedFile.id}
                        itemId={sharedFile.id}
                        containerId="shared-files-base"
                        handle
                        iconAlignY="start"
                        boxContainerStyle={util.mergeStyles(
                          undefined,
                          styles.draggableDocumentGroup,
                          [styles.draggableDocumentGroupLast, isLastItem],
                        )}
                      >
                        <DNABox fill appearance="col">
                          <SharedFilesDocumentGroup
                            sharedFile={sharedFile}
                            isLastItem={true}
                            handleDeleteItem={handleDeleteItem}
                          />
                        </DNABox>
                      </DNARearranger.SortableItem>
                    )
                  })}
                </DNARearranger>
              </Iffy>
            </DNABox>
          </Iffy>
        </DNABox>
      </WidgetCardBody>
    </>
  )
}

const SharedFilesWrapper: React.FC<WidgetContentCardProps> = (props) => {
  return (
    <SharedFilesProvider {...props}>
      <SharedFiles />
    </SharedFilesProvider>
  )
}

export default SharedFilesWrapper
